import { Table, message, TableProps, Row, Col, Spin, Grid } from 'antd';
import { SorterResult } from 'antd/es/table/interface';
import { FilterValue } from 'antd/lib/table/interface';
import config from 'config';
import {
  DonorListFilterInput,
  DonorListSort,
  DonorOutput,
  useGetDonorsListQuery,
  DonorStatus,
  BloodType,
  Order,
  useDeleteReserveDonorNoteMutation,
} from 'gql/types';
import { TableSearchInput } from 'modules/common/components';
import { FilterChips } from 'modules/common/components/FilterChips';
import {
  useSort,
  useDebounce,
  useModal,
  useFilter,
} from 'modules/common/hooks';
import AddDonorNoteModal from 'modules/donor/components/AddDonorNoteModal';
import { TableColumns } from 'modules/donor/components/ReserveDonorsTable/enums';
import {
  createReserveDonorsTableChipsConfig,
  excludedFilterFields,
} from 'modules/donor/configs/reserveDonorsTableFilters';
import { AddDonorNoteModalProps } from 'modules/donor/types';
import { usePagination } from 'modules/navigation/hooks/usePagination';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getColumns } from './columns';

const { useBreakpoint } = Grid;

const ReserveDonorsTable: FC = () => {
  const pagination = usePagination();
  const { debounce, clearDebounce } = useDebounce();
  const donorListSort = useSort<DonorListSort>();
  const reserveDonorsFilters = useFilter<DonorListFilterInput>();
  const addDonorNoteModal = useModal<AddDonorNoteModalProps>();
  const { t } = useTranslation('donor.ReserveDonorsTable');
  const getDonorsListQuery = useGetDonorsListQuery({
    variables: {
      input: {
        skip: pagination.skip,
        take: config.PAGINATION.DEFAULT_LIMIT,
        sort: donorListSort.options,
        filter: {
          ...reserveDonorsFilters.getFilterObject(),
          statuses: [DonorStatus.Trp],
        },
      },
    },
    onCompleted: (result) => {
      pagination.setTotal(result.getDonorsList.meta.total);
    },
    onError: (e) => {
      message.error(e.message);
    },
  });
  const [deleteDonorNoteMutation] = useDeleteReserveDonorNoteMutation();
  const donorList =
    (getDonorsListQuery.data?.getDonorsList.data as DonorOutput[]) || [];

  const chipsConfig = useMemo(
    () => createReserveDonorsTableChipsConfig(t),
    [t]
  );

  const toFilter = (filters: Record<string, FilterValue | null>) => {
    const bloodType = filters[TableColumns.bloodType] as
      | BloodType[]
      | undefined;

    return { bloodType };
  };

  const onTableChange: TableProps<DonorOutput>['onChange'] = (
    _,
    filters,
    sorter,
    extra
  ) => {
    if (extra.action === 'sort') {
      const tableSorter = sorter as SorterResult<DonorOutput>;
      const fieldName = tableSorter.columnKey as DonorListSort;
      const order = tableSorter.order === 'ascend' ? Order.Asc : Order.Desc;
      if (fieldName && order && tableSorter.order) {
        donorListSort.setSortOption({ fieldName, order });
      }
      if (!(sorter as any).order) {
        donorListSort.removeSortOption(fieldName);
      }
    }

    if (extra.action === 'filter') {
      const newFilterData = toFilter(filters);
      Object.keys(newFilterData).forEach((fieldName) => {
        reserveDonorsFilters?.setFilterOption({
          fieldName: fieldName as keyof DonorListFilterInput,
          value: (newFilterData as any)[fieldName],
        });
      });
    }
  };

  const addNewNoteHandler = (donorId: string) => {
    addDonorNoteModal.show({
      noteId: undefined,
      donorId,
      previousValue: undefined,
    });
  };

  const updateNoteHandler = (noteId: string, previousValue: string) => {
    addDonorNoteModal.show({
      noteId,
      previousValue,
    });
  };

  const deleteNoteHandler = async (noteId: string) => {
    await deleteDonorNoteMutation({
      variables: {
        input: {
          id: noteId,
        },
      },
      refetchQueries: ['getDonorsList'],
      onCompleted: () => {
        message.success(t('noteWasDeleted'));
      },
      onError: () => {
        message.error(t('noteWasntDeleted'));
      },
    });
  };

  const handleSearch = (value: string) => {
    pagination.moveTo(1);
    reserveDonorsFilters.setFilterOption({
      fieldName: 'searchingQuery',
      value,
    });
  };

  const { md } = useBreakpoint();
  const columns = getColumns({
    addNewNoteHandler,
    updateNoteHandler,
    deleteNoteHandler,
    showSorterTooltip: md,
    sort: donorListSort?.options[0],
    filter: reserveDonorsFilters?.getFilterObject(),
  });

  useEffect(() => {
    return clearDebounce;
  }, []);

  return (
    <Spin spinning={getDonorsListQuery.loading}>
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <TableSearchInput
            onSearchChange={debounce(handleSearch)}
            placeholder={t('searchDonor')}
            maxLength={100}
          />
        </Col>

        <Col span={24}>
          <FilterChips<DonorListFilterInput>
            filterHook={reserveDonorsFilters}
            config={chipsConfig}
            excludeFields={excludedFilterFields}
          />
        </Col>

        <Col span={24}>
          <Table
            rowKey={({ id }) => id}
            columns={columns}
            dataSource={donorList}
            scroll={{ x: 750 }}
            onChange={onTableChange}
            pagination={{ ...pagination.config }}
          />
        </Col>
      </Row>
      <AddDonorNoteModal {...addDonorNoteModal.config} />
    </Spin>
  );
};

export default ReserveDonorsTable;
